import { Calendar as CalendarIcon } from "lucide-react";
import React, { useState } from "react";

import { CalendarDialog } from "@/components/CalendarDialog";
import { fmtRangeLabel, normalizeRange } from "@/lib/date-range";
import { fmtDate } from "@/lib/format";

/**
 * Интервал дат в ОДНОМ окне: кнопка-значение → модальный календарь, два тапа
 * (начало → конец; обратный порядок разворачивается сам, повторный тап той же
 * даты = один день). Сетка месяца — общая (`CalendarDialog`): нативный
 * `<input type=date>` умеет только одну дату на окно.
 */
export function DateRangePicker({
  from,
  to,
  onChange,
  ariaLabel = "Интервал дат приёмки",
}: {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
  /** Подпись кнопки для читалок и тестов; по умолчанию — приёмки (первый потребитель). */
  ariaLabel?: string;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex h-9 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-sm"
        aria-label={ariaLabel}
      >
        <CalendarIcon className="size-4 shrink-0 text-muted-foreground" />
        {fmtRangeLabel(from, to)}
      </button>
      {open && (
        <RangeCalendar
          from={from}
          to={to}
          onPick={(f, t) => {
            onChange(f, t);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function RangeCalendar({
  from,
  to,
  onPick,
  onClose,
}: {
  from: string;
  to: string;
  onPick: (from: string, to: string) => void;
  onClose: () => void;
}): React.ReactElement {
  // Начатый выбор: первый тап зафиксирован, ждём второй. Пока он есть,
  // подсвечивается ТОЛЬКО он — старый интервал уже не действителен.
  const [pendingStart, setPendingStart] = useState<string | null>(null);

  const click = (iso: string) => {
    if (pendingStart === null) {
      setPendingStart(iso);
      return;
    }
    const [f, t] = normalizeRange(pendingStart, iso);
    onPick(f, t);
  };

  return (
    <CalendarDialog
      initialMonthOf={from}
      isSelected={(iso) =>
        pendingStart === null ? iso === from || iso === to : iso === pendingStart
      }
      isInRange={(iso) => pendingStart === null && from < iso && iso < to}
      onDay={click}
      onClose={onClose}
      hint={
        pendingStart
          ? `С ${fmtDate(pendingStart)} — теперь тапните конечную дату`
          : "Тапните начальную дату, затем конечную (одна и та же — один день)"
      }
    />
  );
}
